import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Contract } from "@visitproof/types";
import { ContractService } from "../services/ContractService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

interface ContractContextProps {
  contractList: Contract[];
  selectedContract: Contract | null;
  setSelectedContract: (contractId: string) => void;
  createContract: (
    contractData: Omit<
      Contract,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ) => Promise<string>;
  updateContract: (
    contractId: string,
    contractData: Partial<Omit<Contract, "id" | "createdAt" | "updatedAt">>,
  ) => void;
  deleteContractById: (contractId: string) => void;
}

const ContractContext = createContext<ContractContextProps | undefined>(
  undefined,
);

export const useContract = (): ContractContextProps => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useContract must be used within a ContractProvider");
  }
  return context;
};

export const ContractProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [contractList, setContractList] = useState<Contract[]>([]);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    null,
  );

  useEffect(() => {
    if (!account?.id) return;
    const docRef = query(
      collection(db, "contracts"),
      where("accountId", "==", account.id),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setContractList(
          docSnapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() }) as Contract,
          ),
        );
      },
      (err) => {
        console.error("Error fetching contracts: ", err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [account]);

  const setSelectedContractById = async (contractId: string) => {
    const contractData = await ContractService.getContract(contractId);
    setSelectedContract(contractData);
  };

  const createContract = async (
    contractData: Omit<
      Contract,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!account?.id || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const newContractId = await ContractService.createContract({
      ...contractData,
      accountId: account.id,
      createdBy: user.uid,
    });
    return newContractId;
  };

  const updateContract = async (
    contractId: string,
    updatedData: Partial<Omit<Contract, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await ContractService.updateContract(contractId, updatedData);
  };

  const deleteContractById = async (contractId: string) => {
    await ContractService.deleteContract(contractId);
  };

  return (
    <ContractContext.Provider
      value={{
        contractList,
        selectedContract,
        createContract,
        updateContract,
        deleteContractById,
        setSelectedContract: setSelectedContractById,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};
